import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, debounceTime, map, switchMap } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth/user-auth/facade';
import { LoginLogoutActionTypes } from '../../../../core/auth/user-auth/store/actions/login-logout.action';
import { ResetSearchAction } from '../../../../core/features/store/actions/search.actions';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../../core/global-message';
import { GlobalMessageActions } from '../../../../core/global-message/store/actions';
import { CurrencyService } from '../../../../core/site-context/facade';
import { normalizeHttpError, withdrawOn } from '../../../../core/util';
import { AppActions } from '../../../../core/util/store/actions';
import { NoOpAction } from '../../../../core/util/store/actions/app.action';
import { BuyerPermissionActions } from '../../../purchase-limits-base/store/actions';
import { SoldToConnector } from '../../connector/sold-to-connector';
import { SoldToActions, SoldToModuleLoadedActions } from '../actions';
import { ChangeSoldToActionTypes } from '../actions/sold-to-change.action';
import { DeliveryAddressActionTypes, DeliveryAddressActions } from '../actions/sold-to-delivery-address.action';
import { DELIVERY_ADDRESS } from '../sold-to-state';

@Injectable()
export class SoldToEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadAvailableSoldTos$: Observable<SoldToActions.LoadAvailableSoldTosSuccess | SoldToActions.LoadAvailableSoldTosFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(SoldToActions.AvailableSoldToActionTypes.LoadAvailableSoldTos),
        debounceTime(10),
        switchMap((action: SoldToActions.LoadAvailableSoldTos) => {
          const params = action.searchParams;
          return this.connector.loadAvailable(params, action.userId).pipe(
            map((res) => new SoldToActions.LoadAvailableSoldTosSuccess(res, params)),
            catchError((error) => of(new SoldToActions.LoadAvailableSoldTosFail(params, normalizeHttpError(error))))
          );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  loadAvailableSoldTosOnUserDetails$: Observable<
    SoldToActions.LoadAvailableSoldTosOnUserDetailsSuccess | SoldToActions.LoadAvailableSoldTosOnUserDetailsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(SoldToActions.AvailableSoldToActionTypes.LoadAvailableSoldTosOnUserDetails),
      switchMap((action: SoldToActions.LoadAvailableSoldTosOnUserDetails) => {
        const params = action.searchParams;
        return this.connector.loadAvailable(params, action.userId).pipe(
          map((res) => new SoldToActions.LoadAvailableSoldTosOnUserDetailsSuccess(res, params)),
          catchError((error) => of(new SoldToActions.LoadAvailableSoldTosOnUserDetailsFail(params, normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  loadActiveSoldTo$: Observable<
    SoldToActions.LoadActiveSoldToSuccess | SoldToActions.LoadActiveSoldToFail | SoldToActions.LoadActiveSoldToCancel
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(SoldToActions.ActiveSoldToActionTypes.LoadActiveSoldTo),
      debounceTime(100),
      switchMap((action: SoldToActions.LoadActiveSoldTo) =>
        this.connector.loadActive(action?.payload).pipe(
          map((data) => {
            if (!action?.oldSoldToId || action?.oldSoldToId !== data?.uid) {
              return new SoldToActions.LoadActiveSoldToSuccess(data);
            }
            return new SoldToActions.LoadActiveSoldToCancel(data);
          }),
          catchError((error) => of(new SoldToActions.LoadActiveSoldToFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadActiveSoldToAtLogin$: Observable<SoldToActions.LoadActiveSoldTo> = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginLogoutActionTypes.Login),
      switchMap(() =>
        this.userIdService.takeUserId(true).pipe(
          map((userId) => new SoldToActions.LoadActiveSoldTo(userId)),
          catchError(() => EMPTY)
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadActiveSoldToAtInitializationIfLoggedIn$: Observable<SoldToActions.LoadActiveSoldTo> = createEffect(() =>
    this.actions$.pipe(
      ofType(SoldToModuleLoadedActions.SOLDTO_MODULE_LOADED),
      switchMap(() =>
        this.userIdService.takeUserId(true).pipe(
          map((userId) => new SoldToActions.LoadActiveSoldTo(userId)),
          catchError(() => EMPTY)
        )
      )
    )
  );

  changeSoldTo$: Observable<
    | SoldToActions.ChangeSoldToSuccess
    | SoldToActions.ChangeSoldToFail
    | GlobalMessageActions.AddMessage
    | BuyerPermissionActions.LoadBuyerPermissionsReset
    | ResetSearchAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeSoldToActionTypes.ChangeSoldTo),
      map((action: SoldToActions.ChangeSoldTo) => action.payload),
      switchMap((payload) =>
        this.connector.change(payload.userId, payload.soldTo).pipe(
          concatMap((data) => [
            new SoldToActions.ChangeSoldToSuccess(data),
            new GlobalMessageActions.AddMessage({
              text: { key: 'myPage.changeCustomerSuccess_message' },
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            }),
            new BuyerPermissionActions.LoadBuyerPermissionsReset(),
            new ResetSearchAction(DELIVERY_ADDRESS),
          ]),
          catchError((error) => of(new SoldToActions.ChangeSoldToFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForChangeSoldTo$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeSoldToActionTypes.ChangeSoldToFail),
      map((action: SoldToActions.ChangeSoldToFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'myPage.changeCustomerFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateActiveSoldToAfterChange$: Observable<SoldToActions.LoadActiveSoldToSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ChangeSoldToActionTypes.ChangeSoldToSuccess),
      map((action: SoldToActions.ChangeSoldToSuccess) => action.payload),
      map((soldTo) => new SoldToActions.LoadActiveSoldToSuccess(soldTo))
    )
  );

  setActiveCurrency$: Observable<NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SoldToActions.ActiveSoldToActionTypes.LoadActiveSoldToSuccess),
        map((action: SoldToActions.LoadActiveSoldToSuccess) => action.payload),
        map((payload) => {
          if (!!payload.currency && !!payload.currency.isocode) {
            /*
        PyLog.feature(PyLogFeatureNames.SOLD_TO)
          .description('Overriding active currency')
          .entry(PyLogEntryNames.SOLDTO, payload.uid)
          .entry('currency', payload.currency.isocode)
          .logInfo(this.logger);
         */
            this.currencyService.setActive(payload.currency.isocode);
          }
          return new AppActions.NoOpAction();
        })
      ),
    { dispatch: false }
  );

  loadDeliveryAddresses$: Observable<DeliveryAddressActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(DeliveryAddressActionTypes.LoadDeliveryAddresses),
      switchMap((action: SoldToActions.LoadDeliveryAddresses) => {
        const params = action.searchParams;
        return this.connector.loadDeliveryAddresses(params, action.userId).pipe(
          map((res) => new SoldToActions.LoadDeliveryAddressesSuccess(res, params)),
          catchError((error) => of(new SoldToActions.LoadDeliveryAddressesFail(params, normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  loadAvailableShipTos$: Observable<SoldToActions.LoadAvailableShipTosSuccess | SoldToActions.LoadAvailableShipTosFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(SoldToActions.AvailableShipTosActionTypes.LoadAvailableShipTos),
        switchMap((action: SoldToActions.LoadAvailableShipTos) => {
          const params = action.searchParams;
          return this.connector.loadAvailableShipTos(params, action.userId).pipe(
            map((res) => new SoldToActions.LoadAvailableShipTosSuccess(res, params)),
            catchError((error) => of(new SoldToActions.LoadAvailableShipTosFail(params, normalizeHttpError(error))))
          );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  constructor(
    private actions$: Actions,
    private connector: SoldToConnector,
    private userIdService: UserIdService,
    private currencyService: CurrencyService
  ) {}
}
